import React, { useCallback, useEffect, useRef, useState } from "react";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Divider, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import AudioService, { AUDIO_CATEGORY } from "../../services/AudioService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import { usePagesTranslation } from "../locales";
import {
    readBgmAutoPlayPreference,
    writeBgmAutoPlayPreference,
} from "./bgmPreference";

const BRAND_GREEN = "#3BA666";

const LEADING_DIVIDER_SX = { mx: 0.25, flexShrink: 0, alignSelf: "stretch" };

function resolveInitialPlaying(defaultPlaying) {
    const stored = readBgmAutoPlayPreference();
    if (stored === false) return false;
    if (stored === true) return defaultPlaying !== false;
    return Boolean(defaultPlaying);
}

function shuffleTracks(list) {
    const out = [...list];
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/**
 * Plays background music with a mute/play toggle.
 *
 * Pass `tracks` for bundled/local URLs (skips API). Otherwise fetches `/home/audio`
 * for `category`, shuffles, and advances randomly on each end.
 *
 * `defaultPlaying` is applied only on mount — remount with `key` when user prefs load asynchronously.
 */
export default function AudioButton({
    active = true,
    category = AUDIO_CATEGORY.BGM,
    /** Optional bundled playlist: `[{ url, name? }]`. When set, API fetch is skipped. */
    tracks: tracksProp = null,
    pageSize = 100,
    sort = "name:asc",
    defaultPlaying = false,
    cloudUserId = null,
    persistPreference = true,
    /** Renders a vertical divider immediately before the icon (only when tracks exist). */
    leadingDivider = false,
    /** Called when the user toggles mute/play (and on mount with the initial playing state). */
    onPlayingChange,
    size = "small",
    sx,
}) {
    const { t } = usePagesTranslation();
    /** Shuffled playlist + cursor for “play next” */
    const playlistRef = useRef({ tracks: [], index: 0 });
    const [trackCount, setTrackCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [playing, setPlaying] = useState(() => resolveInitialPlaying(defaultPlaying));
    const playingRef = useRef(resolveInitialPlaying(defaultPlaying));
    const savingRef = useRef(false);
    const userToggledRef = useRef(false);

    useEffect(() => {
        playingRef.current = playing;
    }, [playing]);

    useEffect(() => {
        onPlayingChange?.(playing);
    }, [playing, onPlayingChange]);

    const persistBgmAutoPlay = useCallback(
        async (nextPlaying) => {
            writeBgmAutoPlayPreference(nextPlaying);
            if (!persistPreference || savingRef.current) return;
            savingRef.current = true;
            try {
                const userInfo = await UserService.getUserInfo();
                const id = cloudUserId || userInfo?.cloudUser?.id;
                if (!id) return;
                await UserService.updateUserProfile(id, {
                    ...UserService.getSettingsFormData(userInfo),
                    bgmAutoPlay: nextPlaying,
                });
            } catch (e) {
                Logger.warn("AudioButton save bgmAutoPlay:", e);
            } finally {
                savingRef.current = false;
            }
        },
        [persistPreference, cloudUserId]
    );

    const playTrackAtIndex = useCallback(
        (index) => {
            if (!playingRef.current) {
                AudioService.stopBgm();
                return;
            }
            const { tracks } = playlistRef.current;
            if (!tracks.length) return;

            const i = ((index % tracks.length) + tracks.length) % tracks.length;
            playlistRef.current.index = i;
            const track = tracks[i];
            if (!track?.url) return;

            const single = tracks.length === 1;
            AudioService.playBgm(track.url, {
                loop: single,
                onEnded: () => {
                    if (!playingRef.current) return;
                    const { tracks: list } = playlistRef.current;
                    if (!list.length) return;
                    let next = Math.floor(Math.random() * list.length);
                    if (list.length > 1) {
                        while (next === playlistRef.current.index) {
                            next = Math.floor(Math.random() * list.length);
                        }
                    }
                    playTrackAtIndex(next);
                },
            });
        },
        []
    );

    const startRandomPlayback = useCallback(() => {
        if (!playingRef.current) return;
        const { tracks } = playlistRef.current;
        if (!tracks.length) return;
        const startIndex = Math.floor(Math.random() * tracks.length);
        playTrackAtIndex(startIndex);
    }, [playTrackAtIndex]);

    const resumeIfNeeded = useCallback(() => {
        if (!playingRef.current || !playlistRef.current.tracks.length) return;
        if (AudioService.isBgmPlaying()) {
            void AudioService.resumeBgm();
            return;
        }
        startRandomPlayback();
    }, [startRandomPlayback]);

    const handleToggle = useCallback(() => {
        const next = !playingRef.current;
        userToggledRef.current = true;
        playingRef.current = next;
        setPlaying(next);
        void persistBgmAutoPlay(next);
        if (next) {
            startRandomPlayback();
        } else {
            AudioService.stopBgm();
        }
    }, [persistBgmAutoPlay, startRandomPlayback]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userInfo = await UserService.getUserInfo();
                if (cancelled || userToggledRef.current) return;
                if (!userInfo?.cloudUser) return;
                const settings = UserService.getSettingsFormData(userInfo);
                const stored = readBgmAutoPlayPreference();
                const allowed = settings.bgmAutoPlay !== false && stored !== false;
                if (settings.bgmAutoPlay === false) {
                    writeBgmAutoPlayPreference(false);
                }
                playingRef.current = allowed;
                setPlaying(allowed);
                if (!allowed) {
                    AudioService.stopBgm();
                }
            } catch (e) {
                Logger.warn("AudioButton load bgmAutoPlay:", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!active) {
            playlistRef.current = { tracks: [], index: 0 };
            setTrackCount(0);
            AudioService.stopBgm();
            return undefined;
        }

        const bundled = Array.isArray(tracksProp)
            ? tracksProp.filter((t) => t?.url && String(t.url).trim())
            : null;

        if (bundled?.length) {
            const shuffled = shuffleTracks(bundled);
            playlistRef.current = { tracks: shuffled, index: 0 };
            setTrackCount(shuffled.length);
            setLoading(false);
            return () => AudioService.stopBgm();
        }

        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const result = await AudioService.listPlayable(category, 0, pageSize, sort);
                if (cancelled) return;
                if (!result.success || !result.data?.content?.length) {
                    playlistRef.current = { tracks: [], index: 0 };
                    setTrackCount(0);
                    return;
                }
                const shuffled = shuffleTracks(result.data.content);
                playlistRef.current = { tracks: shuffled, index: 0 };
                setTrackCount(shuffled.length);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            AudioService.stopBgm();
        };
    }, [active, category, pageSize, sort, tracksProp]);

    useEffect(() => {
        if (!trackCount || !playing) {
            AudioService.stopBgm();
            return undefined;
        }
        startRandomPlayback();
        return () => AudioService.stopBgm();
    }, [trackCount, playing, startRandomPlayback]);

    /** Browsers block autoplay until a gesture; resume on first interaction when user wants BGM on. */
    useEffect(() => {
        if (!active || !trackCount || !playing) return undefined;

        const unlock = () => {
            window.setTimeout(() => resumeIfNeeded(), 0);
        };
        window.addEventListener("pointerdown", unlock, { capture: true, once: true });
        window.addEventListener("keydown", unlock, { capture: true, once: true });
        return () => {
            window.removeEventListener("pointerdown", unlock, { capture: true });
            window.removeEventListener("keydown", unlock, { capture: true });
        };
    }, [active, trackCount, playing, resumeIfNeeded]);

    if (!active) return null;

    const hasTracks = trackCount > 0;
    const showVolumeUp = hasTracks ? playing : playing && loading;
    const tooltip = !hasTracks
        ? t("audio-button.unavailable")
        : playing
          ? t("audio-button.mute")
          : t("audio-button.play");

    return (
        <>
            {leadingDivider ? (
                <Divider orientation="vertical" flexItem sx={LEADING_DIVIDER_SX} />
            ) : null}
            <Tooltip title={tooltip} arrow>
                <span>
                    <IconButton
                        size={size}
                        aria-label={tooltip}
                        onClick={hasTracks ? handleToggle : undefined}
                        disabled={loading || !hasTracks}
                        sx={{
                            color: BRAND_GREEN,
                            "&:hover": { backgroundColor: "rgba(59, 166, 102, 0.12)" },
                            "&.Mui-disabled": { color: "action.disabled" },
                            ...sx,
                        }}
                    >
                        {showVolumeUp ? (
                            <VolumeUpIcon fontSize="small" />
                        ) : (
                            <VolumeOffIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );
}

AudioButton.propTypes = {
    active: PropTypes.bool,
    category: PropTypes.string,
    tracks: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            name: PropTypes.string,
        })
    ),
    pageSize: PropTypes.number,
    sort: PropTypes.string,
    defaultPlaying: PropTypes.bool,
    cloudUserId: PropTypes.string,
    persistPreference: PropTypes.bool,
    leadingDivider: PropTypes.bool,
    onPlayingChange: PropTypes.func,
    size: PropTypes.string,
    sx: PropTypes.object,
};
